import type {
	RegistrationFlow,
	UpdateRegistrationFlowBody,
	UpdateRegistrationFlowWithPasswordMethod,
} from "@ory/kratos-client";
import type { AxiosError } from "axios";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { Flow, handleFlowError } from "~/components/KratosFlow";
import { DefaultLayout } from "~/components/Layout";
import { Loading } from "~/components/Loading";
import { useModal } from "~/components/Modal";
import { useSnackBar } from "~/components/SnackBar";
import { kratos } from "~/ory";
import { expiresAfter, useRegistrationReturnTo } from "~/store";
import { createFlowErrorHandler } from "~/utils/errorHandler";
import { validatePassword } from "~/validator";

export const Registration: React.FC = () => {
	const [agree, setAgree] = useState(false);
	const [flow, setFlow] = useState<RegistrationFlow | undefined>();

	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [openModal, closeModal] = useModal();
	const [openSnackBar] = useSnackBar();
	const [
		_registrationReturnTo, // eslint-disable-line  @typescript-eslint/no-unused-vars
		setRegistrationReturnTo,
		_removeRegistrationReturnTo, // eslint-disable-line  @typescript-eslint/no-unused-vars
	] = useRegistrationReturnTo();

	// Get ?flow=... from the URL
	const returnTo = searchParams.get("return_to");
	const flowId = searchParams.get("flow");

	// In this effect we either initiate a new registration flow, or we fetch an existing registration flow.
	// biome-ignore lint/correctness/useExhaustiveDependencies: openSnackBar/openModal/closeModal are intentionally excluded. They are used only for fire-and-forget error display, and this effect should re-fetch on session/flow/URL changes only. They are stable in production (SnackBar/Modal providers memoize open/close via useCallback), so excluding them changes nothing at runtime; biome just cannot verify that stability across the context boundary.
	useEffect(() => {
		// If the router is not ready yet, or we already have a flow, do nothing.
		if (flow) {
			return;
		}

		// Fetching/initializing a flow can fail at the network level (request aborted,
		// offline) with no HTTP response. handleFlowError can't classify those and re-rejects,
		// so we add a terminal handler to avoid an unhandled promise rejection (mirrors submit()).
		const onFlowError = createFlowErrorHandler(
			openSnackBar,
			openModal,
			closeModal,
		);

		// If ?flow=.. was in the URL, we fetch it
		if (flowId) {
			kratos
				.getRegistrationFlow({ id: String(flowId) })
				.then(({ data }) => {
					// We received the flow - let's use its data and render the form!
					setFlow(data);
				})
				.catch(handleFlowError(navigate, "registration", setFlow))
				.catch(onFlowError);
			return;
		}

		// Otherwise we initialize it
		kratos
			.createBrowserRegistrationFlow({
				returnTo: returnTo ? String(returnTo) : undefined,
			})
			.then(({ data }) => {
				setFlow(data);
			})
			.catch(handleFlowError(navigate, "registration", setFlow))
			.catch(onFlowError);
	}, [flowId, returnTo, flow, navigate]);

	if (!flow) return <Loading />;

	const onSubmit = (values: UpdateRegistrationFlowBody) => {
		setSearchParams({ flow: String(flow?.id) });
		return kratos
			.updateRegistrationFlow({
				flow: String(flow?.id),
				updateRegistrationFlowBody: values,
			})
			.then(() => {
				// If we ended up here, it means we are successfully signed up!
				// For now however we just want to redirect home!
				if (flow?.return_to) {
					setRegistrationReturnTo({
						returnTo: flow.return_to,
						expiresAt: expiresAfter(1),
					});
				}
				window.location.href = "/complete-registration";
			})
			.catch(handleFlowError(navigate, "registration", setFlow))
			.catch((err: AxiosError<RegistrationFlow>) => {
				// If the previous handler did not catch the error it's most likely a form validation error
				if (err.response?.status === 400) {
					// Yup, it is!
					setFlow(err.response?.data);
					return;
				}

				createFlowErrorHandler(openSnackBar, openModal, closeModal)(err);
			});
	};

	return (
		<>
			<title>Registration | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer size="lg">
						<Flow<
							UpdateRegistrationFlowWithPasswordMethod & { method: "password" }
						>
							onSubmit={onSubmit}
							flow={flow}
							sortComponents={[
								{ name: "traits.name.first" },
								{ name: "traits.name.last" },
								{ name: "traits.email" },
								{ name: "password" },
								{ name: "method", type: "submit" },
							]}
							beforeComponents={[
								{
									query: {
										node_type: "input",
										name: "method",
										type: "submit",
									},
									component: () => (
										<TermsAgreementCheckbox
											checked={agree}
											setChecked={setAgree}
										/>
									),
								},
							]}
							afterComponents={[
								{
									query: {
										node_type: "input",
										name: "password",
										type: "password",
									},
									component: () => (
										<div className="mt-1 t4-form-annotation">
											<p>
												In order to protect your account, make sure your
												password must
											</p>
											<p>*have more than 8 characters</p>
										</div>
									),
								},
							]}
							nodeDisablers={[
								{
									target: { name: "method" },
									keys: [
										"traits.name.first" as keyof UpdateRegistrationFlowBody,
										"traits.name.last" as keyof UpdateRegistrationFlowBody,
										"traits.email" as keyof UpdateRegistrationFlowBody,
										"password",
									],
									allowEmpty: false,
									allowNotChanged: true,
									additionalConditionFunc: (): boolean => !agree,
									validators: [
										{
											key: "password",
											validate: (value): boolean => {
												if (typeof value !== "string") return false;
												return validatePassword(value);
											},
										},
									],
								},
							]}
						/>
						<p className="mt-6 text-center">
							<Link to="/login">Go back to Login page</Link>
						</p>
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};

const TermsAgreementCheckbox: React.FC<{
	checked: boolean;
	setChecked: (value: boolean) => void;
}> = ({ checked, setChecked }) => {
	return (
		<div className="form-control mt-8 mb-16">
			<label className="label cursor-pointer justify-start">
				<input
					type="checkbox"
					className="checkbox t4-checkbox"
					checked={checked}
					onChange={(e) => setChecked(e.target.checked)}
				/>
				<span className="pl-2 t4-label-text">
					I have read and agree to the{" "}
					<a
						href="/terms"
						target="_blank"
						rel="noopener noreferrer"
						className="underline text-success"
					>
						terms & conditions
					</a>
				</span>
			</label>
		</div>
	);
};
